import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { useAppSelector, useAppDispatch } from "../../../hooks/hooks";
import { openGlobalPopup, closeGlobalPopup } from "../globalPopupSlice";
import Icon from "../../../components/Icon/Icon";

const SESSION_STORAGE_KEY = "has_seen_global_popup";

const GlobalPopup: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isOpen, imageUrl } = useAppSelector((state) => state.globalPopup);
  const portalRoot = document.body;
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Check for popup image on mount
    const popupImage = window.commonVariables?.popupImage;
    const hasSeen = sessionStorage.getItem(SESSION_STORAGE_KEY);

    if (popupImage && !hasSeen) {
      dispatch(openGlobalPopup(popupImage));
    }
  }, [dispatch]);

  const handleClose = () => {
    dispatch(closeGlobalPopup());
    sessionStorage.setItem(SESSION_STORAGE_KEY, "true");
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") handleClose();
    };

    if (isOpen) {
      document.body.classList.add("global-popup-open");
      document.addEventListener("keydown", handleKeyDown);
    } else {
      document.body.classList.remove("global-popup-open");
    }

    return () => {
      document.body.classList.remove("global-popup-open");
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  // Click outside to close logic
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (contentRef.current && !contentRef.current.contains(e.target as Node)) {
      handleClose();
    }
  };

  if (!isOpen || !portalRoot || !imageUrl) {
    return null;
  }

  return ReactDOM.createPortal(
    <div
      className={`global-popup-wrapper ${isOpen ? "is-open" : ""}`}
      aria-hidden={!isOpen}
    >
      <div className="global-popup__overlay" onClick={handleOverlayClick} />
      <div
        ref={contentRef}
        className="global-popup__content"
        role="dialog"
        aria-modal="true"
      >
        <button
          onClick={handleClose}
          className="global-popup__close-btn"
          aria-label="Close popup"
        >
          <Icon iconName="x-mark" className="icon--stroke-thickest" />
        </button>
        <img src={imageUrl} alt="Announcement" />
      </div>
    </div>,
    portalRoot
  );
};

export default GlobalPopup;
